import {
  Button,
  Input,
  Menu,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise16Regular,
  ArrowDownload16Regular,
  Filter16Regular,
  Info16Regular,
  Search16Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { FlowFilters, FlowList, FlowResult, MetricsRange, TopTalker } from '../api/types';
import type { Download } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { NoAccess } from '../components/NoAccess';
import { PageHeader } from '../components/PageHeader';
import { formatBytes } from '../lib/format';
import { useShell } from '../lib/shell';
import { errorText } from '../lib/useRelay';
import { vt } from '../theme';
import { BarList } from './traffic/BarList';
import { FlowTable } from './traffic/FlowTable';
import { RANGES, colorOf, flowFooter, parseFilter, toBands, toFlowRow, type Stacked } from './traffic/model';
import { StackedChart } from './traffic/StackedChart';

const TALKERS = 5;
/** Rows the flow table asks for; the footer says how many matched in all. */
const FLOW_LIMIT = 50;

/** CONTRACT 4 "Metrics retention": `m:` rows 24 h, `h:` rows 30 d, `d:` rows never pruned. */
export const RETENTION_TEXT = '1-minute samples for 24 h, hourly for 30 days, daily beyond.';

const useStyles = makeStyles({
  tabs: { display: 'flex', columnGap: '4px' },
  tab: {
    height: '28px',
    paddingInline: '12px',
    border: 'none',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'transparent',
    color: vt.fg3,
    fontFamily: 'inherit',
    fontSize: '12.5px',
    cursor: 'pointer',
    ':hover': { color: vt.fg2 },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  tabSelected: { backgroundColor: vt.tintNeutral, color: vt.fg1, fontWeight: tokens.fontWeightSemibold },
  sectionHead: { display: 'flex', alignItems: 'center', columnGap: '16px', minHeight: '30px', marginBottom: '2px' },
  sectionTitle: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  hint: { fontSize: '11.5px', color: vt.fg3, flexGrow: 1 },
  middle: { display: 'grid', gridTemplateColumns: 'minmax(0, 46fr) minmax(0, 54fr)', columnGap: '40px', alignItems: 'start' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', columnGap: '12px' },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '6px',
    padding: '12px 14px',
    borderRadius: '8px',
    backgroundColor: vt.surface3,
    border: `1px solid ${vt.surface2}`,
  },
  tileLabel: { fontSize: '11px', color: vt.fg3 },
  tileValue: { fontFamily: tokens.fontFamilyMonospace, fontSize: '16px', color: vt.fg1 },
  tileDenied: { color: vt.red },
  banner: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    minHeight: '40px',
    marginTop: '12px',
    paddingInline: '14px',
    borderRadius: '8px',
    backgroundColor: vt.tintCyan,
    fontSize: '12px',
    color: vt.fg2,
  },
  bannerIcon: { color: vt.cyan, flexShrink: 0 },
  bannerTitle: { fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  filterForm: { display: 'flex', flexDirection: 'column', rowGap: '4px', width: '260px' },
  filterInput: { width: '100%' },
  filterError: { fontSize: '11px', color: vt.red },
  foot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' },
  footText: { fontSize: '11px', color: vt.fg4 },
  link: {
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: '11.5px',
    color: vt.brand,
    cursor: 'pointer',
    ':hover': { textDecorationLine: 'underline' },
    ':disabled': { color: vt.fg4, cursor: 'default', textDecorationLine: 'none' },
  },
});

/** Hands a downloaded file to the browser as a save. */
function saveDownload({ blob, filename }: Download) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Throughput and top talkers for a range. Loaded on open, on a range change and on Refresh: nothing polls. */
function useMetrics(range: MetricsRange) {
  const { api } = useAuth();
  const [chart, setChart] = useState<Stacked | null>(null);
  const [talkers, setTalkers] = useState<TopTalker[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const [t, top] = await Promise.all([api.throughput(range, 'device'), api.topTalkers(range, TALKERS)]);
      if (mine !== seq.current) return;
      setChart(toBands(t));
      setTalkers(top.talkers);
      setError(null);
    } catch (e) {
      if (mine === seq.current) setError(errorText(e));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [api, range]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { chart, talkers, error, loading, reload };
}

type Filters = Omit<FlowFilters, 'limit'>;

/** GET /flows for the applied filters; the unfiltered answer also feeds the Sessions and Denied tiles. */
function useFlows(filters: Filters) {
  const { api } = useAuth();
  const [list, setList] = useState<FlowList | null>(null);
  const [summary, setSummary] = useState<{ total: number; denied: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const key = JSON.stringify(filters);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    const f = JSON.parse(key) as Filters;
    try {
      const next = await api.listFlows({ ...f, limit: FLOW_LIMIT });
      if (mine !== seq.current) return;
      setList(next);
      if (Object.keys(f).length === 0) setSummary({ total: next.total, denied: next.denied });
      setError(null);
    } catch (e) {
      if (mine === seq.current) setError(errorText(e));
    }
  }, [api, key]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { list, summary, error, reload };
}

/** The active policy's rule ids in order, so "#3" in the filter box can name a rule. */
function useRuleIds(): string[] {
  const { api } = useAuth();
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    api
      .getPolicy()
      .then((p) => live && setIds(p.rules.map((r) => r.id)))
      // Only "#N" needs it; ids and every other filter still work.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api]);
  return ids;
}

const RESULT_LABEL: Record<FlowResult, string> = { allowed: 'Allowed', denied: 'Denied' };

function TrafficScreen() {
  const s = useStyles();
  const { api } = useAuth();
  const { devices } = useShell();
  const [range, setRange] = useState<MetricsRange>('24h');
  const metrics = useMetrics(range);
  const ruleIds = useRuleIds();

  const [text, setText] = useState('');
  const [textFilters, setTextFilters] = useState<Filters>({});
  const [filterErrors, setFilterErrors] = useState<string[]>([]);
  const [result, setResult] = useState<FlowResult | undefined>(undefined);
  const applied = useMemo<Filters>(() => {
    const f: Filters = { ...textFilters };
    if (!f.result && result) f.result = result;
    return f;
  }, [textFilters, result]);
  const flows = useFlows(applied);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const rangeLabel = RANGES.find((r) => r.id === range)!.label;

  const submitFilter = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseFilter(text, devices ?? [], ruleIds);
    setFilterErrors(parsed.errors);
    if (parsed.errors.length === 0) setTextFilters(parsed.filters);
  };

  const viewDenied = () => {
    setText('');
    setTextFilters({});
    setFilterErrors([]);
    setResult('denied');
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      saveDownload(await api.exportFlows(applied));
      setExportError(null);
    } catch (e) {
      setExportError(errorText(e));
    } finally {
      setExporting(false);
    }
  };

  const refresh = () => {
    void metrics.reload();
    void flows.reload();
  };

  const { chart, talkers } = metrics;
  const rows = useMemo(() => (flows.list?.flows ?? []).map(toFlowRow), [flows.list]);
  const sessions = flows.summary ? flows.summary.total - flows.summary.denied : null;

  return (
    <>
      <PageHeader
        title="Traffic"
        actions={
          <>
            <Tooltip content="Refresh" relationship="label">
              <Button appearance="outline" shape="circular" size="small" icon={<ArrowClockwise16Regular />} onClick={refresh} />
            </Tooltip>
            <Menu
              checkedValues={{ result: [result ?? 'all'] }}
              onCheckedValueChange={(_e, d) => {
                const v = d.checkedItems[0];
                setResult(v === 'allowed' || v === 'denied' ? v : undefined);
              }}
            >
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance={result ? 'primary' : 'outline'}
                  shape="circular"
                  size="small"
                  icon={<Filter16Regular />}
                  aria-label="Filter flows by result"
                >
                  {result && RESULT_LABEL[result]}
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItemRadio name="result" value="all">
                    All flows
                  </MenuItemRadio>
                  <MenuItemRadio name="result" value="allowed">
                    Allowed
                  </MenuItemRadio>
                  <MenuItemRadio name="result" value="denied">
                    Denied
                  </MenuItemRadio>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Button
              appearance="outline"
              shape="circular"
              size="small"
              icon={exporting ? <Spinner size="tiny" /> : <ArrowDownload16Regular />}
              disabled={exporting}
              onClick={() => void exportCsv()}
            >
              Export CSV
            </Button>
          </>
        }
      >
        <div className={s.tabs} role="tablist" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={range === r.id}
              className={mergeClasses(s.tab, range === r.id && s.tabSelected)}
              onClick={() => setRange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </PageHeader>

      {exportError && (
        <MessageBar intent="error">
          <MessageBarBody>Export failed: {exportError}</MessageBarBody>
        </MessageBar>
      )}
      {metrics.error && (
        <MessageBar intent="error">
          <MessageBarBody>{metrics.error}</MessageBarBody>
        </MessageBar>
      )}
      {!chart && metrics.loading && <Spinner label="Loading throughput" />}
      {chart && <StackedChart chart={chart} range={range} />}

      <div className={s.middle}>
        <section aria-label="Top talkers">
          <div className={s.sectionHead}>
            <h2 className={s.sectionTitle}>Top talkers</h2>
            <span className={s.hint}>last {rangeLabel}</span>
          </div>
          {talkers && (
            <BarList
              label="Top talkers"
              empty="No device sent or received anything in this range."
              items={talkers.map((t) => ({
                key: t.deviceId,
                label: t.name,
                value: t.total,
                text: formatBytes(t.total),
                color: colorOf(chart, t.deviceId),
              }))}
            />
          )}
        </section>

        <section aria-label="Totals">
          <div className={s.sectionHead}>
            <h2 className={s.sectionTitle}>Totals</h2>
          </div>
          <div className={s.tiles}>
            {(
              [
                ['Received', chart ? formatBytes(chart.rx) : '–', `devices received, last ${rangeLabel}`],
                ['Sent', chart ? formatBytes(chart.tx) : '–', `devices sent, last ${rangeLabel}`],
                ['Sessions', sessions ?? '–', 'allowed flows, last 24 h'],
                ['Denied', flows.summary?.denied ?? '–', 'denied flows, last 24 h'],
              ] as const
            ).map(([label, value, title], i) => (
              <div key={label} className={s.tile} title={title} data-tile={label}>
                <span className={s.tileLabel}>{label}</span>
                <span className={mergeClasses(s.tileValue, i === 3 && s.tileDenied)}>{value}</span>
              </div>
            ))}
          </div>
          <div className={s.banner} role="note" aria-label="Retention">
            <Info16Regular className={s.bannerIcon} />
            <span>
              <span className={s.bannerTitle}>Retention</span> · {RETENTION_TEXT}
            </span>
          </div>
        </section>
      </div>

      <section aria-label="Flows">
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Flows</h2>
          <span className={s.hint}>from the relay’s connection tracking · newest first</span>
          <form className={s.filterForm} onSubmit={submitFilter} role="search">
            <Input
              className={s.filterInput}
              size="small"
              contentBefore={<Search16Regular />}
              placeholder="Filter by device, port or rule"
              aria-label="Filter flows"
              aria-invalid={filterErrors.length > 0}
              value={text}
              onChange={(_e, d) => setText(d.value)}
              onBlur={(e) => submitFilter(e)}
            />
            {filterErrors.length > 0 && (
              <span className={s.filterError} role="alert">
                {filterErrors.join(' · ')}
              </span>
            )}
          </form>
        </div>
        {flows.error && (
          <MessageBar intent="error">
            <MessageBarBody>{flows.error}</MessageBarBody>
          </MessageBar>
        )}
        {!flows.list && !flows.error && <Spinner size="tiny" label="Loading flows" />}
        {flows.list && (
          <>
            <FlowTable rows={rows} />
            <div className={s.foot}>
              <span className={s.footText} data-testid="flow-footer">
                {flowFooter(flows.list.flows.length, flows.list.total, flows.list.denied)}
              </span>
              <button
                type="button"
                className={s.link}
                onClick={viewDenied}
                disabled={result === 'denied' && Object.keys(textFilters).length === 0}
              >
                View all denied
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}

/** Mockup 06, admin only: throughput by device, top talkers, totals and the flow log. */
export function Traffic() {
  const { me, loading, error, isAdmin } = useMe();
  return (
    <>
      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !isAdmin && (
        <>
          <PageHeader title="Traffic" />
          <NoAccess />
        </>
      )}
      {me && isAdmin && <TrafficScreen />}
    </>
  );
}
